import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import userService from '../../services/userService';

const initialState = {
  users: [],
  currentUser: null,
  permissions: [],
  rolePermissions: {},
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  },
  loading: false,
  error: null
};

export const fetchUsers = createAsyncThunk(
  'users/fetchAll',
  async (params, thunkAPI) => {
    try {
      const response = await userService.getUsers(params);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to fetch users';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const fetchUserById = createAsyncThunk(
  'users/fetchById',
  async (id, thunkAPI) => {
    try {
      const response = await userService.getUserById(id);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to fetch user';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const createUser = createAsyncThunk(
  'users/create',
  async (userData, thunkAPI) => {
    try {
      const response = await userService.createUser(userData);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to create user';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const updateUser = createAsyncThunk(
  'users/update',
  async ({ id, data }, thunkAPI) => {
    try {
      const response = await userService.updateUser(id, data);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update user';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const deleteUser = createAsyncThunk(
  'users/delete',
  async (id, thunkAPI) => {
    try {
      await userService.deleteUser(id);
      return id;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete user';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const resetUserPassword = createAsyncThunk(
  'users/resetPassword',
  async ({ id, newPassword }, thunkAPI) => {
    try {
      const response = await userService.resetPassword(id, newPassword);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to reset password';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const fetchPermissions = createAsyncThunk(
  'users/fetchPermissions',
  async (_, thunkAPI) => {
    try {
      const response = await userService.getPermissions();
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to fetch permissions';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

const userSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    clearCurrentUser: (state) => {
      state.currentUser = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.currentUser = action.payload;
      })
      .addCase(createUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.loading = false;
        state.users.unshift(action.payload);
      })
      .addCase(createUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.users.findIndex(u => u.id === action.payload.id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.users = state.users.filter(u => u.id !== action.payload);
      })
      .addCase(fetchPermissions.fulfilled, (state, action) => {
        state.permissions = action.payload.permissions;
        state.rolePermissions = action.payload.rolePermissions;
      });
  }
});

export const { clearCurrentUser, clearError } = userSlice.actions;
export default userSlice.reducer;
